import { IS_AWS_ENV, IS_DEV_ENV, IS_INTERNAL_ENV } from "constants/app.constants";
import {
  ALL_PRODUCT_TYPES,
  ChartProduct,
  DEFAULT_OPACITY,
  InjectionProduct,
  SalesProduct
} from "constants/chart.constants";
import { EvaChart, LiquidProductMinLogYaxis } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { HoverLegends } from "types";
import {
  BestFitToggleFeature,
  HighlightedLineOpacityFeature,
  HighlightedLineSizeFeature,
  StatisticsFeature
} from "types/charts/feature";
import { TypeWellSeriesData } from "types/charts/typeWellSeries";
import { getProductType } from "utils";
import { isLessThanSampleSizeLabel } from "utils/groupBy/isLessThanSampleSizeLabel";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { DataEnum, Format } from "models";
import { EntityKind } from "models/entityKind";
import { DataType } from "models/groupBy";
import { ChartSeries } from "models/model";

import {
  AXIS_LINE_COLOUR,
  AXIS_SPLIT_NUMBER,
  CHART_DEFAULT_MIN_LOG_VALUE,
  CHART_DEFAULT_UNDEFINED_MIN_LOG_VALUE
} from "../../../../constants";
import { ChartLayout } from "../../../../models/chart";
import getLogMinForAxis from "../../../../utils/chart/getLogMinForAxis";
import { getSeriesLabelAliases } from "../../../../utils/chart/seriesMatchesProduct";
import {
  COUNT_TOTAL_GROUP,
  DEFAULT_WIDTH,
  HIGHLIGHTED_WIDTH,
  STATISTIC_SERIES_SET,
  isCountSeries
} from "./constants";

export function getUniqueXQuarters(
  series: ChartSeries[],
  isYearQuarter: boolean,
  xDataType: DataType | undefined
): Set<string> {
  return new Set(
    isYearQuarter && xDataType === DataEnum.Text
      ? series
          .flatMap((s) => s.x as (string | number)[])
          .filter((v): v is string => typeof v === "string" && v.includes("-Q"))
      : []
  );
}

export function formatSingleQuarterLabel(
  year: string,
  month: string,
  singleQuarterTimestamp: number,
  monthToQuarter: Record<string, string>
): string {
  const singleDateStr = new Date(singleQuarterTimestamp).toISOString().substring(0, 10);
  const [singleYear, singleMonth] = singleDateStr.split("-");
  return year === singleYear && month === singleMonth
    ? ` ${year}-${monthToQuarter[month]} `
    : "";
}

export function getLineSeriesMinMax(
  series: ChartSeries[],
  typeWellSeries: TypeWellSeriesData = []
) {
  const seriesData = series || [];
  const allX = seriesData.flatMap((location) => location.x);
  const allY = seriesData.flatMap((location) => location.y);
  const xMinValue = allX.reduce((acc, cur) => (cur < acc ? cur : acc), Infinity);
  const xMaxValue = allX.reduce((acc, cur) => (cur > acc ? cur : acc), -Infinity);
  const yMinValue = allY.reduce((acc, cur) => (cur < acc ? cur : acc), Infinity);
  const yMaxValue = allY.reduce((acc, cur) => (cur > acc ? cur : acc), -Infinity);

  const xMin = { value: xMinValue, type: DataEnum.Number, format: Format.Number };
  const xMax = { value: xMaxValue, type: DataEnum.Number, format: Format.Number };
  const yMin = { value: yMinValue, type: DataEnum.Number, format: Format.Number };
  const yMax = { value: yMaxValue, type: DataEnum.Number, format: Format.Number };

  // Include type well series in min/max calculations
  let finalXMin = xMin;
  let finalXMax = xMax;
  let finalYMin = yMin;
  let finalYMax = yMax;

  if (typeWellSeries.length > 0) {
    // Type well data can be from line or scatter series - extract as generic arrays
    const typeWellData = typeWellSeries.flatMap(
      (tw) => (tw.data as Array<[number, number] | [number, number, number]>) || []
    );
    const typeWellX = typeWellData.map((point) => point[0]);
    const typeWellY = typeWellData.map((point) => point[1]);

    if (typeWellX.length > 0 && typeWellY.length > 0) {
      const twXMin = Math.min(...typeWellX);
      const twXMax = Math.max(...typeWellX);
      const twYMin = Math.min(...typeWellY);
      const twYMax = Math.max(...typeWellY);

      finalXMin = { ...xMin, value: Math.min(xMin.value, twXMin) };
      finalXMax = { ...xMax, value: Math.max(xMax.value, twXMax) };
      finalYMin = { ...yMin, value: Math.min(yMin.value, twYMin) };
      finalYMax = { ...yMax, value: Math.max(yMax.value, twYMax) };
    }
  }

  return { xMin: finalXMin, xMax: finalXMax, yMin: finalYMin, yMax: finalYMax };
}
export function getTimeSeriesMinMax(
  series: { x: (string | number)[]; y: number[] }[],
  format: Format
) {
  const seriesData = series || [];
  const latestDate = new Date(Date.UTC(275760, 8, 13));
  const earliestDate = new Date(Date.UTC(-271821, 4, 20));

  const allX = seriesData.flatMap((location) => location.x);
  const allDatesX = allX.map((date) => {
    if (typeof date !== "string") {
      return;
    }
    const yearMonthDateDate = date.split("T")[0];
    const [year, month, day] = yearMonthDateDate.split("-").map(Number);
    return new Date(Date.UTC(year, (month || 1) - 1, day || 1));
  });
  const allY = seriesData.flatMap((location) => location.y);

  const xMinValue = allDatesX.reduce((acc, cur) => (cur < acc ? cur : acc), latestDate);
  const xMaxValue = allDatesX.reduce((acc, cur) => (cur > acc ? cur : acc), earliestDate);
  const yMinValue = allY.reduce((acc, cur) => (cur < acc ? cur : acc), Infinity);
  const yMaxValue = allY.reduce((acc, cur) => (cur > acc ? cur : acc), -Infinity);

  const formatDateToYYYYMMDD = (date: Date) =>
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(
      date.getUTCDate()
    ).padStart(2, "0")}`;

  let xMin, xMax;
  if (format === Format.Day) {
    xMin = {
      value: formatDateToYYYYMMDD(xMinValue),
      type: DataEnum.Date,
      format: Format.Day
    };
    xMax = {
      value: formatDateToYYYYMMDD(xMaxValue),
      type: DataEnum.Date,
      format: Format.Day
    };
  } else if (format === Format.Month) {
    xMin = {
      value: formatDateToYYYYMMDD(xMinValue).slice(0, 7), // Extract YYYY-MM
      type: DataEnum.Date,
      format: Format.Month
    };
    xMax = {
      value: formatDateToYYYYMMDD(xMaxValue).slice(0, 7), // Extract YYYY-MM
      type: DataEnum.Date,
      format: Format.Month
    };
  } else if (format === Format.Year) {
    xMin = {
      value: xMinValue.getUTCFullYear(),
      type: DataEnum.Date,
      format: Format.Year
    };
    xMax = {
      value: xMaxValue.getUTCFullYear(),
      type: DataEnum.Date,
      format: Format.Year
    };
  } else {
    xMin = {
      value: formatDateToYYYYMMDD(xMinValue),
      type: DataEnum.Date,
      format: Format.Day
    };
    xMax = {
      value: formatDateToYYYYMMDD(xMaxValue),
      type: DataEnum.Date,
      format: Format.Day
    };
  }
  const yMin = { value: yMinValue, type: DataEnum.Number, format: Format.Number };
  const yMax = { value: yMaxValue, type: DataEnum.Number, format: Format.Number };

  return { xMin, xMax, yMin, yMax };
}

/** Returns the indices of xSeries entries that fall within [xMinFilter, xMaxFilter].
 *  Falls back to all indices when no filter is applied or the filter matches nothing. */
function getVisibleIndices(
  xSeries: string[],
  xMinFilter?: string | number,
  xMaxFilter?: string | number
): { allDatesX: Date[]; indicesToUse: number[] } {
  const allDatesX = xSeries.map((date) => new Date(date));
  const xMinDate = xMinFilter != null ? new Date(xMinFilter) : null;
  const xMaxDate = xMaxFilter != null ? new Date(xMaxFilter) : null;

  const visibleIndices = allDatesX.reduce<number[]>((acc, date, i) => {
    if ((!xMinDate || date >= xMinDate) && (!xMaxDate || date <= xMaxDate)) {
      acc.push(i);
    }
    return acc;
  }, []);

  const indicesToUse =
    visibleIndices.length > 0 ? visibleIndices : allDatesX.map((_, i) => i);

  return { allDatesX, indicesToUse };
}

export function getTotalRateDateMinMax(
  series: ChartSeries[],
  xSeries: string[],
  xMinFilter?: string | number,
  xMaxFilter?: string | number
) {
  const seriesData = series || [];
  const latestDate = new Date(8640000000000000);
  const earliestDate = new Date(-8640000000000000);

  const { allDatesX, indicesToUse } = getVisibleIndices(xSeries, xMinFilter, xMaxFilter);

  const nonWellCountSeries = seriesData.filter(
    ({ label }) => label.toLowerCase() !== "well count"
  );

  // Sum y values across all non-well-count series for each visible index only
  const summedYValues = indicesToUse.map((idx) =>
    nonWellCountSeries.reduce(
      (sum, { y }) => sum + (isNaN(Number(y[idx])) ? 0 : Number(y[idx])),
      0
    )
  );

  const visibleDates = indicesToUse.map((i) => allDatesX[i]);
  const xMinValue = visibleDates.reduce(
    (acc, cur) => (cur < acc ? cur : acc),
    latestDate
  );
  const xMaxValue = visibleDates.reduce(
    (acc, cur) => (cur > acc ? cur : acc),
    earliestDate
  );
  const yMinValue = 0;
  const yMaxValue = summedYValues.length > 0 ? Math.max(...summedYValues) : 0; // max of visible window only

  const xMin = {
    value: xMinValue.toISOString().split("T")[0].slice(0, 7),
    type: DataEnum.Date,
    format: Format.Month
  };
  const xMax = {
    value: xMaxValue.toISOString().split("T")[0].slice(0, 7),
    type: DataEnum.Date,
    format: Format.Month
  };
  const yMin = { value: yMinValue, type: DataEnum.Number, format: Format.Number };
  const yMax = { value: yMaxValue, type: DataEnum.Number, format: Format.Number };

  return { xMin, xMax, yMin, yMax };
}

/**
 * Returns the max well/facility count value within the visible X window.
 */
export function getVisibleWellCountMax(
  series: ChartSeries[],
  xSeries: string[],
  xMinFilter?: string | number,
  xMaxFilter?: string | number
): number | undefined {
  // EVA-6819: in multi-product mode `series` holds the summed count plus one per
  // product. The axis max must come from the SUMMED count (groupTitle === COUNT_TOTAL_GROUP),
  // not whichever count series happens to be first. Fall back to the first count series for
  // single-product / pre-"Aggregate" backends.
  const wellCountSeries =
    series.find(
      ({ label, groupTitle }) => isCountSeries(label) && groupTitle === COUNT_TOTAL_GROUP
    ) ?? series.find(({ label }) => isCountSeries(label));
  if (!wellCountSeries) return undefined;

  const { indicesToUse } = getVisibleIndices(xSeries, xMinFilter, xMaxFilter);

  const visibleValues = indicesToUse
    .map((i) => wellCountSeries.y[i])
    .filter((v) => v != null && !isNaN(Number(v)))
    .map(Number);

  return visibleValues.length > 0 ? Math.max(...visibleValues) : undefined;
}

/**
 * Returns the next "nice" tick mark above `value`, using ECharts-compatible
 * interval snapping (1 / 2 / 5 × power-of-10, targeting ~5 ticks from 0).
 * e.g. value=31 → interval=10 → 40; value=100 → interval=20 → 120.
 */
export function computeNiceYMax(value: number): number {
  if (value <= 0) return 1;
  const roughInterval = value / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));
  const normalized = roughInterval / magnitude;
  let niceInterval: number;
  if (normalized <= 1) niceInterval = magnitude;
  else if (normalized <= 2) niceInterval = 2 * magnitude;
  else if (normalized <= 5) niceInterval = 5 * magnitude;
  else niceInterval = 10 * magnitude;
  return (Math.floor(value / niceInterval) + 1) * niceInterval;
}

/**
 * Returns the next power of 10 above `value` for log-scale axes
 * whose ticks are 0.01, 0.1, 1, 10, 100, 1000 …
 * e.g. value=6 → 10; value=50 → 100; value=100 → 1000.
 */
export function computeNiceLogYMax(value: number): number {
  if (value <= 0) return 10;
  return Math.pow(10, Math.floor(Math.log10(value)) + 1);
}

export function getStackedBarChartMinMax(
  series: { x: (string | number)[]; y: Nullable<number>[] }[]
) {
  const seriesData = series || [];
  const columnSum = new Map<string, number>();

  seriesData.forEach((item) => {
    item.x.forEach((xValue, index) => {
      const yValue = item.y[index];
      const key = xValue.toString();

      const currentSum = columnSum.get(key) || 0;
      columnSum.set(key, currentSum + (yValue ?? 0));
    });
  });

  // Loop instead of Math.max(...values) - spreading a large series blows the call stack.
  let yMaxValue = 0;
  let yMinValue = 0;
  let seenFirst = false;
  columnSum.forEach((sum) => {
    if (!seenFirst) {
      yMaxValue = sum;
      yMinValue = sum;
      seenFirst = true;
      return;
    }
    if (sum > yMaxValue) yMaxValue = sum;
    if (sum < yMinValue) yMinValue = sum;
  });

  const yMin = { value: yMinValue, type: DataEnum.Number };
  const yMax = { value: yMaxValue, type: DataEnum.Number };
  return { yMin, yMax };
}

// Where a group sits after the named bins in rank order: no value, Less Than, Others.
const getRankTail = (label: string): number => {
  if (isOthersGroupLabel(label)) return 3;
  if (isLessThanSampleSizeLabel(label)) return 2;
  const lower = label.toLowerCase();
  return lower === "n/a" || lower === "subscription required" ? 1 : 0;
};

/**
 * The stacked-bar categories in x-axis order. Desc and Asc sort by the stacked bar total.
 * Unsorted keeps the response order, except when the category field has Max Bins and the
 * response carries the rank values: then it is the Max Bins rank order, with the groups
 * that fold or lack a value (n/a, Less Than, Others) last.
 */
export function getStackedBarPositions(
  labels: (string | number)[],
  series: { data?: unknown }[],
  sortValue: Maybe<string>,
  categoryMaxBins: Maybe<number | string>,
  maxBinsSortValues: Maybe<Record<string, number>>
): { label: string | number; index: number; total: number }[] {
  const positions = labels.map((label, index) => {
    const total = series.reduce<number>((sum, s) => {
      const value = Array.isArray(s.data) && s.data[index] ? s.data[index] : 0;
      return sum + (value || 0);
    }, 0);
    return { label, index, total };
  });

  if (sortValue === "desc") return positions.sort((a, b) => b.total - a.total);
  if (sortValue === "asc") return positions.sort((a, b) => a.total - b.total);
  if (!(Number(categoryMaxBins) > 0) || !maxBinsSortValues) return positions;

  // A named group the service gave no value ranks after every valued one.
  const rank = (label: string | number) =>
    maxBinsSortValues[String(label)] ?? Number.NEGATIVE_INFINITY;
  return positions.sort((a, b) => {
    const tail = getRankTail(String(a.label)) - getRankTail(String(b.label));
    if (tail) return tail;
    const [rankA, rankB] = [rank(a.label), rank(b.label)];
    return rankA === rankB ? 0 : rankB > rankA ? 1 : -1;
  });
}

export function computeSelectionStates({
  hoverLegendItem,
  hoverLegendGroup,
  attentionWells,
  label,
  groupTitle,
  isSpaghetti = false,
  selectedGroups = [],
  isMultiProductActive = false
}: Partial<HoverLegends>): { isSelectedWell: boolean; isHovered: boolean } {
  const hasAttentionWell = attentionWells?.length > 0;
  const selectedWell = hasAttentionWell ? attentionWells[0] : null;

  const isSelectedWell = selectedWell ? label?.includes(selectedWell) ?? false : false;

  const isGroupHovered =
    (groupTitle === hoverLegendGroup && !isSpaghetti) ||
    groupTitle === hoverLegendItem ||
    (groupTitle != null && selectedGroups.includes(groupTitle)) ||
    (isMultiProductActive &&
      selectedGroups.some((selectedGroup) => groupTitle?.includes(selectedGroup))) ||
    // for multi product, needed for forecasts
    (isMultiProductActive &&
      !!hoverLegendItem &&
      groupTitle != null &&
      hoverLegendItem.includes(groupTitle)) ||
    // for multi product, highlighting on focus/map
    (isMultiProductActive && !!hoverLegendItem && groupTitle?.includes(hoverLegendItem));
  const isHovered =
    selectedWell &&
    (hoverLegendItem === selectedWell ||
      // for multi product spaghetti highlighting when hovering over chart
      (isMultiProductActive && hoverLegendItem?.includes(selectedWell)))
      ? isSelectedWell
      : isGroupHovered;
  return { isSelectedWell, isHovered };
}

export function computeSyncedWellState({
  label,
  syncWells,
  hoverLegendItem,
  isSpaghetti = false
}: Partial<HoverLegends>): { isSyncedWell: boolean } {
  let syncedWell = null;

  if (
    IS_INTERNAL_ENV &&
    syncWells?.activeWells?.length > 0 &&
    syncWells?.isSync &&
    syncWells?.highlightSyncedWell !== false &&
    isSpaghetti &&
    // if another well is hovered, do not highlight the synced well
    !(hoverLegendItem !== "" && hoverLegendItem !== syncWells?.activeWells[0])
  ) {
    syncedWell = syncWells?.activeWells[0];
  }

  const isSyncedWell = syncedWell && label.includes(syncedWell);

  return { isSyncedWell };
}

export function getGroupNameFromLabel(label: string): string {
  const match = label.match(/^(.*)\sForecast/);
  return match ? match[1].trim() : label;
}

const isValue = (value: unknown): value is number =>
  typeof value === "number" && !isNaN(value);

// EVA-389 is not released: only dev-aws draws the join and the month-end history.
export const isGapJoinEnabled = (): boolean => IS_AWS_ENV && IS_DEV_ENV;

const GAP_ID_SUFFIX = {
  join: ", gap",
  spacer: ", gap spacer",
  edge: ", gap edge"
} as const;
export type GapBridgeKind = keyof typeof GAP_ID_SUFFIX;

export const isGapBridgeId = (id: unknown): boolean =>
  typeof id === "string" &&
  Object.values(GAP_ID_SUFFIX).some((suffix) => id.endsWith(suffix));

type GapPoint = [string | number, number | null];
export type AreaEdgeColors = { rest: string; hover: string };
export type GapBridge = {
  id: string;
  group: string;
  source: ChartSeries;
  data: GapPoint[];
} & ({ kind: "join" | "spacer" } | { kind: "edge"; edgeColors: AreaEdgeColors });

// ECharts' default areaStyle opacity and emphasis liftColor, assuming a white background.
const AREA_OPACITY = 0.7;
const toRgb = (c: number[]) => `rgb(${c.map(Math.round).join(",")})`;
const overWhite = (c: number[]) =>
  c.map((v) => v * AREA_OPACITY + 255 * (1 - AREA_OPACITY));
const lifted = (c: number[]) => c.map((v) => Math.min(255, Math.floor(v * 1.1)));

// Null unless 6-digit hex: an opaque strip in a guessed colour would show.
export function getAreaEdgeColors(hex: string): Nullable<AreaEdgeColors> {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return { rest: toRgb(overWhite(c)), hover: toRgb(overWhite(lifted(c))) };
}

// Per group: a join from the last history date to the next (forecast) date, drawn as
// history in its own stack, which spacers keep level, and an edge strip over its seam.
// Boundary dates only: a time axis stacks by index, so joins and spacers share one list.
// historyX is where history is drawn, so a join and its edge start on its points.
export function getGapBridges(
  series: ChartSeries[],
  x: (string | number)[],
  historyX: (string | number)[] = x
): GapBridge[] {
  if (!isGapJoinEnabled()) return [];
  // By groupTitle, as a title containing "Forecast" defeats label parsing. chart-svc sends
  // each group's series together, so this order matches the main stack.
  const groups = new Map<string, ChartSeries[]>();
  for (const s of series.filter((s) => !isCountSeries(s.label))) {
    const key = s.groupTitle || getGroupNameFromLabel(s.label);
    groups.set(key, [...(groups.get(key) ?? []), s]);
  }

  // Next date only: across a wider gap, the groups stacked above drop into this slot.
  const joins = new Map<string, number>();
  for (const [key, members] of groups) {
    const from = Math.max(
      -1,
      ...members.filter((s) => !s.isForecast).map((s) => s.y.findLastIndex(isValue))
    );
    if (from >= 0 && members.some((s) => s.isForecast && isValue(s.y[from + 1]))) {
      joins.set(key, from);
    }
  }
  const junctions = [
    ...new Set([...joins.values()].flatMap((from) => [from, from + 1]))
  ].sort((a, b) => a - b);

  const bridges: GapBridge[] = [];
  const stackAt = new Map(junctions.map((i) => [i, 0]));
  for (const [key, members] of groups) {
    const valueAt = (i: number) =>
      members.reduce((sum, s) => sum + (isValue(s.y[i]) ? s.y[i] : 0), 0);
    const from = joins.get(key) ?? -1;
    const isJoined = (i: number) => from >= 0 && (i === from || i === from + 1);
    const source = members.find((s) => !s.isForecast) ?? members[0];
    // Named as the main series are, so hovering a join highlights the same group.
    const group = getGroupNameFromLabel(source.label);
    const id = (kind: GapBridgeKind) =>
      kind === "spacer"
        ? `${key}${GAP_ID_SUFFIX.spacer}`
        : `${group}, ${source.label}${GAP_ID_SUFFIX[kind]}`;

    for (const kind of ["join", "spacer"] as const) {
      const values = junctions.map((i) =>
        isJoined(i) === (kind === "join") ? valueAt(i) : null
      );
      if (values.some((value) => (value ?? 0) !== 0)) {
        const data = values.map((value, n): GapPoint => {
          const i = junctions[n];
          return [i === from ? historyX[i] : x[i], value];
        });
        bridges.push({ id: id(kind), group, source, kind, data });
      }
    }
    const edgeColors = getAreaEdgeColors(source.style?.hexColor ?? "");
    if (from >= 0 && valueAt(from) > 0 && edgeColors) {
      const base = stackAt.get(from) ?? 0;
      const data: GapPoint[] = [
        [historyX[from], base],
        [historyX[from], base + valueAt(from)]
      ];
      bridges.push({ id: id("edge"), group, source, kind: "edge", data, edgeColors });
    }
    for (const i of junctions) stackAt.set(i, (stackAt.get(i) ?? 0) + valueAt(i));
  }
  return bridges;
}

export function computeScatterSelectionStates({
  hoverLegendItem,
  selectedGroups = [],
  label
}: Partial<HoverLegends>): { isHovered: boolean } {
  const isHovered = label === hoverLegendItem || selectedGroups.includes(label);
  return { isHovered };
}

export function computeMaterialBalanceSelectionStates({
  hoverLegendItem,
  selectedGroups = [],
  label,
  groupTitle = "",
  attentionWells = []
}: Partial<HoverLegends>): { isHovered: boolean } {
  if (hoverLegendItem && selectedGroups.length === 0 && attentionWells.length === 0) {
    const isHovered = label.includes(hoverLegendItem) || groupTitle === hoverLegendItem;
    return { isHovered };
  }

  const isGroupMatched =
    selectedGroups.includes(groupTitle) ||
    label.includes(hoverLegendItem) ||
    groupTitle === hoverLegendItem;
  const isWellMatched =
    attentionWells.length > 0 ? label.includes(attentionWells[0]) : false;
  const isHovered =
    attentionWells.length > 0 ? isGroupMatched && isWellMatched : isGroupMatched;
  return { isHovered };
}

/**
 * Creates default Y-axis options for logarithmic scale charts.
 *
 * @param {ChartLayout} layout - The chart layout configuration.
 * @param {number | undefined} lockedYMin - Optional locked minimum value for Y-axis.
 *                                         If provided, overrides the calculated minimum.
 * @param {number | undefined} lockedYMax - Optional locked maximum value for Y-axis.
 *                                         If provided, sets the maximum bound.
 * @returns {Partial<YAXisOption>} Configuration object for Y-axis with logarithmic scale settings.
 */
export function getDefaultYAxisLogOptions(
  layout: ChartLayout,
  lockedYMin: number | undefined,
  lockedYMax: number | undefined,
  chartType: EvaChart,
  product: string | undefined,
  entityKind: EntityKind | undefined
): Partial<YAXisOption> {
  const yAxis: Partial<YAXisOption> = {
    type: "log"
  };

  const isInvalidLock = lockedYMin === undefined || Number(lockedYMin) <= 0.0;

  if (isInvalidLock) {
    if (shouldLimitLogYAxisForLiquidProducts(product, entityKind)) {
      // Use chart-specific liquid product minimums for undefined or invalid locks
      if (
        chartType === EvaChart.RateTime ||
        chartType === EvaChart.RateCum ||
        chartType === EvaChart.TotalRateCum
      ) {
        yAxis.min = LiquidProductMinLogYaxis.Point1;
      } else if (
        chartType === EvaChart.RateDate ||
        chartType === EvaChart.TotalRateDate
      ) {
        yAxis.min = LiquidProductMinLogYaxis.Point01;
      }
    }
  } else {
    // Valid positive lock value provided
    yAxis.min = Number(lockedYMin);
  }

  if (yAxis.min === undefined) {
    if (lockedYMin === undefined) {
      yAxis.min = getLogMinForAxis(layout) ?? CHART_DEFAULT_UNDEFINED_MIN_LOG_VALUE;
    } else {
      // lockedYMin was provided but invalid (≤ 0)
      yAxis.min = CHART_DEFAULT_MIN_LOG_VALUE;
    }
  }

  if (lockedYMax !== undefined) {
    yAxis.max = Number(lockedYMax);
  }
  // Minor gridlines are important when looking at a plot in log scale for accurate data interpretation.
  yAxis.minorSplitLine = {
    show: true,
    lineStyle: { color: AXIS_LINE_COLOUR }
  };
  yAxis.minorTick = {
    splitNumber: AXIS_SPLIT_NUMBER
  };

  return yAxis;
}

/**
 * Creates default X-axis options for logarithmic scale charts.
 *
 * @param {number | undefined} lockedXMin - Optional locked minimum value for X-axis.
 *                                         If provided, overrides the calculated minimum.
 * @param {number | undefined} lockedXMax - Optional locked maximum value for X-axis.
 *                                         If provided, sets the maximum bound.
 * @returns {Partial<XAXisOption>} Configuration object for X-axis with logarithmic scale settings.
 */
export function getDefaultXAxisLogOptions(
  lockedXMin: number | undefined,
  lockedXMax: number | undefined
): Partial<XAXisOption> {
  return {
    type: "log",
    min: lockedXMin ?? 1,
    max: lockedXMax ?? null,
    minorSplitLine: {
      show: true,
      lineStyle: { color: AXIS_LINE_COLOUR }
    },
    minorTick: {
      splitNumber: AXIS_SPLIT_NUMBER
    }
  };
}

const SUBSCRIPT_PRODUCTS = {
  "Oil, Cond, C5+": "Oil, Cond, C{sub|5+}",
  NGL_234: "NGL{sub|234}",
  C2: "C{sub|2}",
  C3: "C{sub|3}",
  C4: "C{sub|4}",
  "C5+": "C{sub|5+}",
  C5: "C{sub|5+}",
  CO2: "CO{sub|2}"
};

export function formatChartTitleWithSubscript(title: string): string {
  if (!title) {
    return title;
  }
  const sortedProducts = Object.entries(SUBSCRIPT_PRODUCTS).sort(
    (a, b) => b[0].length - a[0].length
  );

  return sortedProducts.reduce((formattedTitle, [product, richText]) => {
    const escapedProduct = product.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedProduct, "g");
    return formattedTitle.replace(regex, richText);
  }, title);
}

// The Unicode counterpart of a {sub|...} rich-text run. ECharts renders the rich form;
// plain DOM text cannot, so the same digits have to exist as real characters.
const UNICODE_SUBSCRIPTS: Record<string, string> = {
  "0": "₀",
  "1": "₁",
  "2": "₂",
  "3": "₃",
  "4": "₄",
  "5": "₅",
  "6": "₆",
  "7": "₇",
  "8": "₈",
  "9": "₉",
  "+": "₊"
};

/**
 * The Unicode-subscript rendering of a product name, for DOM text that cannot use
 * ECharts rich text: "NGL_234" becomes "NGL₂₃₄", "C2 Yields Raw" becomes
 * "C₂ Yields Raw".
 *
 * Built on formatChartTitleWithSubscript so the two renderings cannot drift -- SUBSCRIPT_PRODUCTS
 * stays the single source of truth for which products subscript and how. Only the digits
 * change; every other word is left exactly as the chart service wrote it.
 */
export function toUnicodeSubscript(text: string): string {
  if (!text) return text;
  return formatChartTitleWithSubscript(text).replace(
    /\{sub\|([^}]*)\}/g,
    (_match, sub: string) =>
      [...sub].map((char) => UNICODE_SUBSCRIPTS[char] ?? char).join("")
  );
}

// Which parenthesized groups in a series label are product names at all. Every key,
// label and backend alias a product can be written as, kept only when subscripting
// actually changes it.
//
// A gate is needed because formatChartTitleWithSubscript rewrites its patterns anywhere
// in a string: without it a bin named "AC2 Field" would come out "AC₂ Field". Membership
// is the only thing read from this set -- the replacement text comes from subscripting the
// token itself, never from a label lookup. ALL_PRODUCT_TYPES keys are NOT unique (the well
// "C2 Yields Raw" and the midstream "C2 Yields Inlet" share the key "C2 Yields Raw"), so a
// key -> label lookup here would hand well charts the facility label.
const SUBSCRIPTABLE_PRODUCT_TOKENS: Set<string> = (() => {
  const tokens = new Set<string>();
  const entries = Object.values(ALL_PRODUCT_TYPES) as Array<{
    key: string;
    label: string;
  }>;
  entries.forEach(({ key, label }) => {
    [key, label, ...getSeriesLabelAliases(key)].forEach((token) => {
      if (token && toUnicodeSubscript(token) !== token) tokens.add(token);
    });
  });
  return tokens;
})();

/**
 * Subscript the product token the chart service writes inside a series label:
 * "(113) Dunvegan (NGL_234)" becomes "(113) Dunvegan (NGL₂₃₄)".
 *
 * The DOM legend renders a series label verbatim, so unlike an ECharts axis title nothing
 * turns the ASCII form into the name the product selector shows. Only a parenthesized
 * group that EXACTLY matches a known product token is rewritten, so the well count "(113)"
 * and any surrounding bin name are untouched, and only the digits within it change --
 * "C2 Yields Raw" keeps its words.
 *
 * Presentation only: callers format at render and keep the stored title, which
 * legend/map hover matching compares against. Pure.
 */
export function subscriptProductTokens(seriesLabel: string): string {
  if (!seriesLabel) return seriesLabel;
  return seriesLabel.replace(/\(([^()]+)\)/g, (match, token: string) =>
    SUBSCRIPTABLE_PRODUCT_TOKENS.has(token) ? `(${toUnicodeSubscript(token)})` : match
  );
}

/**
 * Extracts highlighted line size value from feature with fallback to default highlighted width
 */
export function getHighlightedLineSizeValue(
  highlightedLineSize?: Partial<HighlightedLineSizeFeature>
): number {
  return highlightedLineSize?.properties?.value ?? HIGHLIGHTED_WIDTH;
}

/**
 * Extracts highlighted line opacity value from feature with fallback to default
 */
export function getHighlightedLineOpacityValue(
  highlightedLineOpacity?: Partial<HighlightedLineOpacityFeature>
): number {
  return highlightedLineOpacity?.properties?.value ?? DEFAULT_OPACITY;
}

/**
 * Determines whether the log Y-axis should be limited for liquid products
 *
 * @param product - The product identifier string (e.g., "Oil", "Gas", "Water")
 * @param entityKind - The entity kind (Well or Facility) to determine product context
 * @returns {boolean} {boolean} True if the product is a liquid type and the log Y-axis should be limited; otherwise, false.
 *
 * @example
 * ```typescript
 * const shouldLimit = shouldLimitLogYAxisForLiquidProducts("Oil", EntityKind.Well);
 * console.log(shouldLimit); // Returns true
 * ```
 */
export function shouldLimitLogYAxisForLiquidProducts(
  product: string | undefined,
  entityKind: EntityKind | undefined
): boolean {
  if (!product || !entityKind) {
    return false;
  }

  // Get the product type using existing utility
  const productType = getProductType(product, entityKind);
  const productLabel = productType.label; // Check which product list contains this product's label
  if (
    ChartProduct.find((category) => category.name === "Product")?.items.includes(
      productLabel
    )
  ) {
    return true;
  } else if (
    SalesProduct.find((category) => category.name === "Liquids")
      ?.items.concat([
        ALL_PRODUCT_TYPES.SalesGas.label,
        ALL_PRODUCT_TYPES.GasEnergy.label
      ])
      .includes(productLabel)
  ) {
    return true;
  } else if (
    InjectionProduct.find((category) => category.name === "Product")?.items.includes(
      productLabel
    )
  ) {
    return true;
  }

  return false;
}

/**
 * Extracts thickness value from a statistics or best fit feature
 * @param feature - The feature object containing thickness properties
 * @returns Thickness value or default width if not found
 */
export function getFeatureThickness(
  feature?: Partial<{ properties: { value?: number; thickness?: number } }>
): number {
  const properties = feature?.properties;
  return properties?.value ?? properties?.thickness ?? DEFAULT_WIDTH;
}

/**
 * Extracts opacity value from a statistics or best fit feature
 * @param feature - The feature object containing opacity properties
 * @param defaultOpacity - Default opacity to use if not found
 * @returns Opacity value or default if not found
 */
export function getFeatureOpacity(
  feature?: Partial<{ properties: { opacity?: number } }>,
  defaultOpacity: number = DEFAULT_OPACITY
): number {
  return feature?.properties?.opacity ?? defaultOpacity;
}

export const isStatisticsLabel = (label: string): boolean => {
  return (
    STATISTIC_SERIES_SET.has(label) ||
    Array.from(STATISTIC_SERIES_SET).some(
      (stat) => label.includes(stat) || label.includes(`${stat} Forecast`)
    )
  );
};

const DAYS_TO_5_YEARS = 1825;
const DAYS_TO_YEARS = 365.25;
const DAYS_TO_6_MONTHS = 182.625;
const DAYS_TO_3_MONTHS = 91.3125;
const DAYS_PER_MONTH = 30;
const DAYS_PER_HALF_MONTH = 15;
const DAYS_PER_WEEK = 7;

//number of months before switching to years
const MONTHS_PER_20_YEARS = 240;
const MONTHS_PER_12_YEARS = 144;
const MONTHS_PER_8_YEARS = 96;
const MONTHS_PER_5_YEARS = 60;
const MONTHS_PER_3_YEARS = 36;
const MONTHS_PER_2_YEARS = 24;
const MONTHS_PER_1_YEAR = 12;
const MONTHS_PER_HALF_YEAR = 6;
const MONTHS_PER_QUARTER_YEAR = 3;

export function setXAxisParams(
  isDailyData: boolean,
  isXLogAxisOn: boolean,
  layoutTitle: string,
  xMaxValue: number,
  xMinValue: number
) {
  let xAxisTitle = layoutTitle;
  let interval = undefined;
  let formatter = (value: number) => {
    return value.toString();
  };

  if (isXLogAxisOn) {
    return { xAxisTitle, interval, formatter };
  }

  if (isDailyData) {
    const axisLength = Math.abs(xMaxValue - xMinValue);

    let selectedInterval: number;
    let selectedModulo: number;
    let selectedType: string = "day";

    if (axisLength <= DAYS_PER_HALF_MONTH) {
      selectedInterval = 1;
      selectedModulo = 1;
    } else if (axisLength <= DAYS_PER_MONTH) {
      selectedInterval = 2;
      selectedModulo = 2;
    } else if (axisLength <= DAYS_TO_3_MONTHS) {
      selectedInterval = DAYS_PER_WEEK;
      selectedModulo = DAYS_PER_WEEK;
    } else if (axisLength <= DAYS_TO_6_MONTHS) {
      selectedInterval = DAYS_PER_HALF_MONTH;
      selectedModulo = DAYS_PER_HALF_MONTH;
    } else if (axisLength <= DAYS_TO_YEARS) {
      selectedInterval = DAYS_PER_MONTH;
      selectedModulo = DAYS_PER_MONTH;
    } else if (axisLength <= DAYS_TO_5_YEARS) {
      selectedInterval = DAYS_TO_3_MONTHS;
      selectedModulo = DAYS_TO_3_MONTHS;
      selectedType = "month";
      xAxisTitle = layoutTitle.replace("day", "month");
    } else {
      selectedInterval = DAYS_TO_YEARS;
      selectedModulo = DAYS_TO_YEARS;
      selectedType = "year";
      xAxisTitle = layoutTitle.replace("day", "year");
    }

    interval = selectedInterval;

    formatter = (value: number) => {
      if (value % selectedModulo === 0) {
        if (selectedType === "year") {
          const years = Math.round(value / DAYS_TO_YEARS);
          return ` ${years.toString()} `;
        } else if (selectedType === "month") {
          const months = Math.floor(value / (DAYS_PER_MONTH + 0.4)); //add 0.4 for more accurate month labels
          return ` ${months.toString()} `;
        } else {
          return ` ${value.toString()} `;
        }
      }
      return "";
    };
  } else {
    const axisLength = Math.abs(xMaxValue - xMinValue);
    const shouldShowXMaxLabel = axisLength < MONTHS_PER_2_YEARS;

    let selectedInterval: number;
    let selectedModulo: number;
    let useYearLabels: boolean = false;
    xAxisTitle = layoutTitle;

    if (axisLength <= MONTHS_PER_1_YEAR) {
      selectedInterval = 1;
      selectedModulo = 1;
    } else if (axisLength <= MONTHS_PER_3_YEARS) {
      selectedInterval = MONTHS_PER_QUARTER_YEAR;
      selectedModulo = MONTHS_PER_QUARTER_YEAR;
    } else if (axisLength <= MONTHS_PER_8_YEARS) {
      selectedInterval = MONTHS_PER_HALF_YEAR;
      selectedModulo = MONTHS_PER_HALF_YEAR;
    } else if (axisLength <= MONTHS_PER_12_YEARS) {
      selectedInterval = MONTHS_PER_1_YEAR;
      selectedModulo = MONTHS_PER_1_YEAR;
      useYearLabels = true;
      xAxisTitle = layoutTitle.replace("month", "year");
    } else if (axisLength <= MONTHS_PER_20_YEARS) {
      selectedInterval = MONTHS_PER_2_YEARS;
      selectedModulo = MONTHS_PER_2_YEARS;
      useYearLabels = true;
      xAxisTitle = layoutTitle.replace("month", "year");
    } else {
      selectedInterval = MONTHS_PER_5_YEARS;
      selectedModulo = MONTHS_PER_5_YEARS;
      useYearLabels = true;
      xAxisTitle = layoutTitle.replace("month", "year");
    }

    interval = selectedInterval;

    formatter = (value: number) => {
      if (value === xMaxValue && shouldShowXMaxLabel) {
        return ` ${value.toString()} `;
      }
      if (value % selectedModulo === 0) {
        if (useYearLabels) {
          const years = value / MONTHS_PER_1_YEAR;
          return ` ${years.toString()} `;
        } else {
          return ` ${value.toString()} `;
        }
      }
      return "";
    };
  }

  return { xAxisTitle, interval, formatter };
}
/**
 * Configuration object containing all statistical and best fit feature definitions
 */
export interface SeriesFeatures {
  average?: Partial<StatisticsFeature>;
  p10?: Partial<StatisticsFeature>;
  p50?: Partial<StatisticsFeature>;
  p90?: Partial<StatisticsFeature>;
  bestFitToggle?: Partial<BestFitToggleFeature>;
}

/**
 * Style properties for chart series (thickness and opacity)
 */
export interface SeriesStyle {
  thickness: number;
  opacity: number;
}

const MAX_AXES_PER_SIDE = 2;

/**
 * Limits the number of y-axes to a maximum of 2 per side (left/right).
 * Keeps the first 2 axes encountered for each position value.
 */
export function limitYAxesPerSide<T extends { position: string }>(axes: T[]): T[] {
  const countByPosition: Record<string, number> = {};
  return axes.filter((axis) => {
    const pos = axis.position;
    countByPosition[pos] = (countByPosition[pos] ?? 0) + 1;
    return countByPosition[pos] <= MAX_AXES_PER_SIDE;
  });
}

/** Floating-point tolerance for treating a value as an exact power of 10. */
const LOG10_POWER_TOLERANCE = 0.01;

/**
 * Minimum log₁₀-space distance between a power-of-10 label and a non-power-of-10
 * boundary before the power-of-10 label is suppressed. 0.4 ≈ 2.5× ratio.
 * e.g. max=20,000 (log₁₀=4.301) hides "10,000" (log₁₀=4) since 0.301 < 0.4.
 */
const LOG_AXIS_LABEL_MIN_LOG_DISTANCE = 0.4;

/**
 * Creates a label formatter for log-scale Y-axes that always shows boundary
 * labels (user lock values) and hides power-of-10 labels that are too close
 * to a non-power-of-10 boundary.
 */
export function createLogAxisLabelFormatter(
  axisMin: number | undefined,
  axisMax: number | undefined
): (value: number) => string {
  return (value: number): string => {
    if (value <= 0) return "";

    const log = Math.log10(value);
    const isPowerOf10 = Math.abs(log - Math.round(log)) < LOG10_POWER_TOLERANCE;

    // Non-power-of-10 labels are boundary ticks (user lock values) — always show
    if (!isPowerOf10) {
      return value.toLocaleString("en-US");
    }

    // For power-of-10 labels, check proximity to a non-power-of-10 boundary
    if (axisMax !== undefined && axisMax > 0) {
      const maxLog = Math.log10(axisMax);
      const maxIsPowerOf10 =
        Math.abs(maxLog - Math.round(maxLog)) < LOG10_POWER_TOLERANCE;
      if (!maxIsPowerOf10 && Math.abs(log - maxLog) < LOG_AXIS_LABEL_MIN_LOG_DISTANCE) {
        return "";
      }
    }

    if (axisMin !== undefined && axisMin > 0) {
      const minLog = Math.log10(axisMin);
      const minIsPowerOf10 =
        Math.abs(minLog - Math.round(minLog)) < LOG10_POWER_TOLERANCE;
      if (!minIsPowerOf10 && Math.abs(log - minLog) < LOG_AXIS_LABEL_MIN_LOG_DISTANCE) {
        return "";
      }
    }

    return value.toLocaleString("en-US");
  };
}
